import type { InfoOF } from "@/hooks/Api/OrdenesFabricacion";
import { TypedTable } from "@/components/ui/table/TypedTable";
import { useMemo } from "react";
import { createColumnHelper, tableFeatures } from "@tanstack/react-table";
import { CommonFeatures } from "@/hooks/useTanstackTable";

interface TablaOrdenesProps {
	readonly data: InfoOF[];
	readonly error: Error | null;
	readonly isPending: boolean;
}
export function TablaOrdenes(props: TablaOrdenesProps) {
	const { data, error, isPending } = props;

	const { columns, features } = useMemo(() => Columns(), []);

	const dataExtendida = useMemo(() => data.map(extendInfoOF), [data]);

	return (
		<TypedTable
			className="dataTable display"
			data={dataExtendida}
			columns={columns}
			features={features}

			noResultsMessage="No se encontraron resultados"
			errorMessage={error?.message ?? "Error al cargar las órdenes de fabricación"}
			isLoading={isPending}
			isError={error != null}

			pagination
		/>
	);
}

function Columns() {
	const columnHelper = createColumnHelper<typeof features, InfoOFExtendida>();
	const features = tableFeatures(CommonFeatures);

	const formatter = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2, minimumFractionDigits: 0 });

	const columns = columnHelper.columns([
		columnHelper.accessor("idOF", { header: "Número de OF", meta: { className: "dt-center" } }),
		columnHelper.display({ header: "Conjunto", cell: info => `${info.row.original.idConjunto}-${info.row.original.descripcion}`, meta: { className: "dt-center" } }),
		columnHelper.accessor("situacion", {
			header: "Situación",
			cell: (info) => {
				const of = info.row.original;

				if (of.situacion === 2)
					return `Cerrada (${formatter.format(of.percent)}%)`;
				else if (of.situacion === 0)
					return "Sin iniciar";
				else
					return `En curso (${formatter.format(of.percent)}%)`;
			},
			sortFn: (rowA, rowB) => {
				if (rowA.original.situacion < rowB.original.situacion) return -1;
				if (rowA.original.situacion > rowB.original.situacion) return 1;
				if (rowA.original.percent < rowB.original.percent) return -1;
				if (rowA.original.percent > rowB.original.percent) return 1;
				return 0;
			},
		}),
		columnHelper.accessor("cantLanzada", { header: "Cantidad Lanzada", cell: info => info.getValue().toLocaleString("es-ES"), meta: { className: "dt-center" } }),
		columnHelper.accessor("cantAcabada", { header: "Cantidad Terminada", cell: info => info.getValue().toLocaleString("es-ES"), meta: { className: "dt-center" } }),

	]);
	return { columns, features };
}


type InfoOFExtendida = InfoOF & { situacion: 0 | 1 | 2; percent: number; };
function extendInfoOF(orden: InfoOF): InfoOFExtendida {
	let horasCalculadas = 0;
	let horasImputadas = 0;
	for (const fase of orden.fases) {
		const tiempoEjecucion = (fase.tiempoEjecucionMano ?? 0) + (fase.tiempoEjecucionMaq ?? 0);
		horasCalculadas += (fase.cantLanzada || orden.cantLanzada) * tiempoEjecucion;
		horasImputadas += fase.cantAcabada * tiempoEjecucion;
	}

	const porcentaje = (horasImputadas / horasCalculadas) * 100;

	const cerrada = orden.fases.every(fase => fase.timestampFin != null);
	const sinAbrir = orden.fases.every(fase => fase.timestampIni == null);

	return {
		...orden,
		// eslint-disable-next-line no-nested-ternary -- Simple enough
		situacion: cerrada ? 2 : sinAbrir ? 0 : 1,
		percent: porcentaje,
	};
}